/* ============================================================
   Dathent — Admin Behavior (/app/admin/behavior)
   TASK-167 (#273), child of EPIC-018 (#200). Charts the activation funnel
   (funnel_events, TASK-122/#214, migration 0104) that pages/dathent_ai
   src/lib/funnel.js already emits into. Gated server-side by
   requireAdminSession() in dathent-api-gateway-worker src/routes/
   admin-behavior.ts (checks GET /api/auth/me's isAdmin flag — same signal
   RequireAdmin/Sidebar use); this screen carries no client guard of its own
   beyond the route-level <RequireAdmin> in App.jsx, mirroring AdminUsers.jsx.

   No charting library — a small hand-rolled SVG polyline chart, same "no
   heavy dependencies" rule the owner set for this screen (issue #273). Every
   read uses cacheTTL:0 (AdminBehaviorClient in services/api/client.js): this
   is an ops tool, not a cached view.
   ============================================================ */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, DathentAPIError } from '../services/api/client.js';

// Canonical funnel stage order — mirrors FUNNEL_EVENT_NAMES in
// dathent-api-gateway-worker src/routes/admin-behavior.ts. Keep these two
// lists in sync; the backend 400s any name outside this set.
const FUNNEL_STAGES = [
  { key: 'signup_completed', label: 'Signup completed' },
  { key: 'onboarding_started', label: 'Onboarding started' },
  { key: 'onboarding_skipped', label: 'Onboarding skipped' },
  { key: 'onboarding_completed', label: 'Onboarding completed' },
  { key: 'social_connected', label: 'Social connected' },
  { key: 'first_generation', label: 'First generation' },
  { key: 'first_publish', label: 'First publish' },
  { key: 'preview_paywall_shown', label: 'Paywall shown' },
  { key: 'paywall_dismissed', label: 'Paywall dismissed' },
  { key: 'subscribe_clicked', label: 'Subscribe clicked' },
  { key: 'checkout_completed', label: 'Checkout completed' },
  { key: 'nudge_shown', label: 'Nudge shown' },
  { key: 'nudge_clicked', label: 'Nudge clicked' },
  { key: 'tour_started', label: 'Tour started' },
  { key: 'tour_completed', label: 'Tour completed' },
  { key: 'tour_skipped', label: 'Tour skipped' },
  { key: 'screen_view', label: 'Screen view' },
];
const STAGE_LABEL = Object.fromEntries(FUNNEL_STAGES.map((s) => [s.key, s.label]));

// Default selection: the primary signup -> paid conversion path, without the
// side-funnels (nudge/tour effectiveness, generic screen views) — those are
// still one click away via the multiselect.
const DEFAULT_STAGES = [
  'signup_completed', 'onboarding_started', 'onboarding_completed',
  'social_connected', 'first_generation', 'first_publish',
  'preview_paywall_shown', 'subscribe_clicked', 'checkout_completed',
];

const CHART_COLORS = [
  '#caa24a', '#7ee2a8', '#7fb8e8', '#e8a59f', '#c99ee8',
  '#e7c878', '#78c9d6', '#e89f6b', '#9fd67e',
];

const DEFAULT_EVENTS_LIMIT = 100;

function isoDay(date) {
  return date.toISOString().slice(0, 10);
}

function daysAgo(n) {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - n);
  return isoDay(d);
}

const PRESETS = [
  { key: '7d', label: '7d', from: () => daysAgo(6), to: () => daysAgo(0) },
  { key: '30d', label: '30d', from: () => daysAgo(29), to: () => daysAgo(0) },
  { key: '90d', label: '90d', from: () => daysAgo(89), to: () => daysAgo(0) },
];

function errMsg(e) {
  if (e instanceof DathentAPIError) return e.message || 'Request failed';
  return e?.message || 'Request failed';
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function fmtShortDay(iso) {
  try {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

// ─── style primitives (mirrors AdminUsers.jsx) ─────────────────────────────

const card = {
  background: 'var(--surface, #16161a)',
  border: '1px solid var(--border, #2a2a30)',
  borderRadius: 14,
  padding: 20,
};
const label = { display: 'block', fontSize: 12, color: 'var(--text-muted, #9a9aa2)', margin: '10px 0 4px' };
const input = {
  width: '100%', boxSizing: 'border-box', background: 'var(--surface-2, #0f0f12)',
  border: '1px solid var(--border, #2a2a30)', borderRadius: 10, color: 'var(--text, #ececf0)',
  padding: '9px 11px', fontSize: 13.5, fontFamily: 'inherit',
};
const btn = (disabled) => ({
  background: 'var(--gold, #caa24a)', color: '#0b0b0c', border: 0, borderRadius: 10,
  padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});
const btnGhost = (disabled, active) => ({
  background: active ? 'var(--surface-2, #0f0f12)' : 'transparent',
  color: active ? 'var(--text, #ececf0)' : 'var(--text-muted, #9a9aa2)',
  border: `1px solid ${active ? 'var(--gold, #caa24a)' : 'var(--border, #2a2a30)'}`,
  borderRadius: 10, padding: '7px 13px', fontSize: 12.5, fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
});
const chip = (active, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
  border: `1px solid ${active ? `${color}88` : 'var(--border, #2a2a30)'}`,
  background: active ? `${color}18` : 'transparent',
  color: active ? color : 'var(--text-muted, #9a9aa2)',
  borderRadius: 999, padding: '4px 11px', cursor: 'pointer', userSelect: 'none',
});

function ErrorNote({ error }) {
  if (!error) return null;
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 13,
      background: '#e0685f18', border: '1px solid #e0685f55', color: '#e8a59f',
    }}>{error}</div>
  );
}

// ─── filters ────────────────────────────────────────────────────────────────

function FiltersPanel({ from, to, setFrom, setTo, activePreset, onPreset, stages, toggleStage, onSelectAll, onSelectNone, disabled }) {
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        <div>
          <label style={label}>Period</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                style={btnGhost(disabled, activePreset === p.key)}
                disabled={disabled}
                onClick={() => onPreset(p)}
              >
                {p.label}
              </button>
            ))}
            <input type="date" style={{ ...input, width: 150 }} value={from} onChange={(e) => setFrom(e.target.value)} disabled={disabled} />
            <span style={{ color: 'var(--text-muted, #9a9aa2)', fontSize: 13 }}>→</span>
            <input type="date" style={{ ...input, width: 150 }} value={to} onChange={(e) => setTo(e.target.value)} disabled={disabled} />
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ ...label, margin: 0 }}>Funnel stages</label>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onSelectAll} disabled={disabled} style={{ background: 'none', border: 0, color: 'var(--gold, #caa24a)', fontSize: 12, cursor: 'pointer' }}>Select all</button>
            <button type="button" onClick={onSelectNone} disabled={disabled} style={{ background: 'none', border: 0, color: 'var(--text-muted, #9a9aa2)', fontSize: 12, cursor: 'pointer' }}>Clear</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {FUNNEL_STAGES.map((s, i) => {
            const active = stages.includes(s.key);
            const color = CHART_COLORS[FUNNEL_STAGES.filter((x) => stages.includes(x.key)).findIndex((x) => x.key === s.key) % CHART_COLORS.length] || CHART_COLORS[i % CHART_COLORS.length];
            return (
              <span key={s.key} style={chip(active, color)} onClick={() => !disabled && toggleStage(s.key)}>
                {active && <span style={{ width: 7, height: 7, borderRadius: 999, background: color }} />}
                {s.label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── chart ──────────────────────────────────────────────────────────────────

function FunnelChart({ series, stages }) {
  const width = 860;
  const height = 280;
  const padding = { top: 14, right: 16, bottom: 26, left: 44 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const [hoverIdx, setHoverIdx] = useState(null);

  const days = series.length;
  const maxVal = Math.max(1, ...series.flatMap((p) => stages.map((s) => p.counts[s] || 0)));

  const xFor = (i) => padding.left + (days <= 1 ? innerW / 2 : (i / (days - 1)) * innerW);
  const yFor = (v) => padding.top + innerH - (Math.min(v, maxVal) / maxVal) * innerH;

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width) return;
    const relX = ((e.clientX - rect.left) / rect.width) * width;
    const ratio = days <= 1 ? 0 : (relX - padding.left) / innerW;
    const idx = Math.round(ratio * (days - 1));
    setHoverIdx(Math.min(Math.max(idx, 0), days - 1));
  };

  const gridFractions = [0, 0.25, 0.5, 0.75, 1];
  const hoverPoint = hoverIdx != null ? series[hoverIdx] : null;

  if (days === 0 || stages.length === 0) {
    return <div style={{ ...card, color: 'var(--text-muted, #9a9aa2)', textAlign: 'center' }}>Pick at least one stage to chart.</div>;
  }

  return (
    <div style={card}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {gridFractions.map((f) => {
          const y = padding.top + innerH * (1 - f);
          return (
            <g key={f}>
              <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke="var(--border, #2a2a30)" strokeWidth="1" />
              <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill="var(--text-muted, #9a9aa2)">
                {Math.round(maxVal * f)}
              </text>
            </g>
          );
        })}

        {stages.map((s, si) => {
          const color = CHART_COLORS[si % CHART_COLORS.length];
          const points = series.map((p, i) => `${xFor(i)},${yFor(p.counts[s] || 0)}`).join(' ');
          return (
            <g key={s}>
              <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              {series.map((p, i) => (
                <circle key={i} cx={xFor(i)} cy={yFor(p.counts[s] || 0)} r={hoverIdx === i ? 3.5 : 0} fill={color} />
              ))}
            </g>
          );
        })}

        {hoverIdx != null && (
          <line x1={xFor(hoverIdx)} x2={xFor(hoverIdx)} y1={padding.top} y2={padding.top + innerH} stroke="var(--text-muted, #9a9aa2)" strokeDasharray="3,3" />
        )}

        {[0, Math.floor((days - 1) / 2), days - 1].map((i, k) => (
          <text key={k} x={xFor(i)} y={height - 6} textAnchor={i === 0 ? 'start' : i === days - 1 ? 'end' : 'middle'} fontSize="10" fill="var(--text-muted, #9a9aa2)">
            {fmtShortDay(series[i].date)}
          </text>
        ))}
      </svg>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 12 }}>
        {stages.map((s, si) => (
          <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted, #9a9aa2)' }}>
            <span style={{ width: 9, height: 9, borderRadius: 999, background: CHART_COLORS[si % CHART_COLORS.length] }} />
            {STAGE_LABEL[s] || s}
          </span>
        ))}
      </div>

      {/* Hover readout */}
      <div style={{
        marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 12.5,
        background: 'var(--surface-2, #0f0f12)', border: '1px solid var(--border, #2a2a30)',
        color: 'var(--text-muted, #9a9aa2)', minHeight: 20,
      }}>
        {hoverPoint ? (
          <span>
            <strong style={{ color: 'var(--text, #ececf0)' }}>{fmtShortDay(hoverPoint.date)}</strong>
            {' — '}
            {stages.map((s, i) => (
              <span key={s} style={{ marginRight: 14 }}>
                <span style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}>{STAGE_LABEL[s] || s}</span>: {hoverPoint.counts[s] || 0}
              </span>
            ))}
          </span>
        ) : 'Hover the chart for daily values.'}
      </div>
    </div>
  );
}

// ─── summary table (totals + step-to-step conversion) ──────────────────────

function SummaryTable({ stages, totals }) {
  if (stages.length === 0) return null;
  const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #9a9aa2)', padding: '0 10px 8px', whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderTop: '1px solid var(--border, #2a2a30)', whiteSpace: 'nowrap' };

  let prevTotal = null;
  const rows = stages.map((s) => {
    const total = totals[s] || 0;
    const conv = prevTotal != null && prevTotal > 0 ? (total / prevTotal) * 100 : null;
    prevTotal = total;
    return { key: s, label: STAGE_LABEL[s] || s, total, conv };
  });

  return (
    <div style={{ ...card, padding: 0, overflowX: 'auto', marginTop: 16 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...th, paddingLeft: 20 }}>Stage</th>
            <th style={th}>Total</th>
            <th style={{ ...th, paddingRight: 20 }}>Step → step conversion</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key}>
              <td style={{ ...td, paddingLeft: 20 }}>{r.label}</td>
              <td style={td}><strong>{r.total}</strong></td>
              <td style={{ ...td, paddingRight: 20, color: 'var(--text-muted, #9a9aa2)' }}>
                {i === 0 ? '—' : r.conv == null ? '—' : `${r.conv.toFixed(1)}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SOURCE_FUNNEL_STAGES = [
  ['onboarding_completed', 'Onboarding completed'],
  ['onboarding_skipped', 'Onboarding skipped'],
  ['card_added', 'Card added'],
  ['chatgpt_returned', 'ChatGPT returned'],
  ['first_post_created', 'First post created'],
];

function SourceFunnelTable({ state }) {
  const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #9a9aa2)', padding: '0 10px 8px', whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderTop: '1px solid var(--border, #2a2a30)', whiteSpace: 'nowrap' };
  const value = (row, key) => {
    const count = Number(row[key] || 0);
    const pct = row.signups > 0 ? (count / row.signups) * 100 : 0;
    return `${count} (${pct.toFixed(1)}%)`;
  };

  return (
    <div style={{ ...card, padding: 0, overflowX: 'auto', marginTop: 16 }}>
      <div style={{ padding: '14px 20px 10px', fontSize: 13, fontWeight: 600 }}>30-day funnel by signup source</div>
      {state.loading ? (
        <div style={{ padding: 20, color: 'var(--text-muted, #9a9aa2)' }}>Loading source funnel…</div>
      ) : state.error ? (
        <div style={{ padding: '0 20px 16px' }}><ErrorNote error={state.error} /></div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, paddingLeft: 20 }}>Source</th>
            <th style={th}>Signups</th>
            {SOURCE_FUNNEL_STAGES.map(([key, title]) => <th key={key} style={th}>{title}</th>)}
          </tr></thead>
          <tbody>{state.rows.map((row) => (
            <tr key={row.source}>
              <td style={{ ...td, paddingLeft: 20 }}><strong>{row.source}</strong></td>
              <td style={td}>{row.signups}</td>
              {SOURCE_FUNNEL_STAGES.map(([key]) => <td key={key} style={td}>{value(row, key)}</td>)}
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

// ─── raw events table ───────────────────────────────────────────────────────

function EventsTable({ events, loading, error, total }) {
  const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #9a9aa2)', padding: '0 10px 8px', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12.5, borderTop: '1px solid var(--border, #2a2a30)' };

  return (
    <div style={{ ...card, padding: 0, overflowX: 'auto', marginTop: 16 }}>
      <div style={{ padding: '14px 20px 0', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
        <span>Recent raw events</span>
        <span style={{ color: 'var(--text-muted, #9a9aa2)', fontWeight: 400 }}>{total} total in range</span>
      </div>
      {loading ? (
        <div style={{ padding: 20, color: 'var(--text-muted, #9a9aa2)' }}>Loading events…</div>
      ) : error ? (
        <div style={{ padding: '0 20px 16px' }}><ErrorNote error={error} /></div>
      ) : events.length === 0 ? (
        <div style={{ padding: 20, color: 'var(--text-muted, #9a9aa2)' }}>No events match this range.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
          <thead>
            <tr>
              <th style={{ ...th, paddingLeft: 20 }}>Time</th>
              <th style={th}>Name</th>
              <th style={th}>User</th>
              <th style={th}>Path</th>
              <th style={{ ...th, paddingRight: 20 }}>Props</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td style={{ ...td, paddingLeft: 20, whiteSpace: 'nowrap', color: 'var(--text-muted, #9a9aa2)' }}>{fmtDateTime(e.createdAt)}</td>
                <td style={td}>{e.name}</td>
                <td style={{ ...td, color: 'var(--text-muted, #9a9aa2)' }}>{e.userId || '—'}</td>
                <td style={{ ...td, color: 'var(--text-muted, #9a9aa2)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.path || '—'}</td>
                <td style={{ ...td, paddingRight: 20, color: 'var(--text-muted, #9a9aa2)', fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 11 }}>
                  {e.props ? JSON.stringify(e.props) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── CSV export ─────────────────────────────────────────────────────────────

function toCsv(series, stages) {
  const header = ['date', ...stages].join(',');
  const rows = series.map((p) => [p.date, ...stages.map((s) => p.counts[s] || 0)].join(','));
  return [header, ...rows].join('\n');
}

function downloadCsv(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── screen shell ───────────────────────────────────────────────────────────

export function AdminBehavior() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [activePreset, setActivePreset] = useState('30d');
  const [stages, setStages] = useState(DEFAULT_STAGES);

  const [funnelState, setFunnelState] = useState({ loading: true, error: null, series: [], totals: {} });
  const [eventsState, setEventsState] = useState({ loading: true, error: null, events: [], total: 0 });
  const [sourceState, setSourceState] = useState({ loading: true, error: null, rows: [] });

  const loadFunnel = useCallback(async () => {
    setFunnelState((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const res = await api.adminBehavior.funnel({ from, to, groupBy: 'day', names: stages });
      setFunnelState({ loading: false, error: null, series: res.series || [], totals: res.totals || {} });
    } catch (e) {
      setFunnelState((cur) => ({ ...cur, loading: false, error: errMsg(e) }));
    }
  }, [from, to, stages]);

  const loadEvents = useCallback(async () => {
    setEventsState((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const res = await api.adminBehavior.events({ from, to, limit: DEFAULT_EVENTS_LIMIT });
      setEventsState({ loading: false, error: null, events: res.events || [], total: res.total ?? 0 });
    } catch (e) {
      setEventsState((cur) => ({ ...cur, loading: false, error: errMsg(e) }));
    }
  }, [from, to]);

  useEffect(() => { loadFunnel(); }, [loadFunnel]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => {
    api.adminUsers.funnel(30)
      .then((res) => setSourceState({ loading: false, error: null, rows: res.rows || [] }))
      .catch((e) => setSourceState({ loading: false, error: errMsg(e), rows: [] }));
  }, []);

  const onPreset = (p) => {
    setActivePreset(p.key);
    setFrom(p.from());
    setTo(p.to());
  };
  // Any manual date edit detaches from the preset highlight.
  const setFromManual = (v) => { setActivePreset(null); setFrom(v); };
  const setToManual = (v) => { setActivePreset(null); setTo(v); };

  const toggleStage = (key) => {
    setStages((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };
  const orderedStages = useMemo(
    () => FUNNEL_STAGES.map((s) => s.key).filter((k) => stages.includes(k)),
    [stages]
  );

  const onExport = () => {
    const csv = toCsv(funnelState.series, orderedStages);
    downloadCsv(csv, `dathent-funnel-${from}-to-${to}.csv`);
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '8px 4px 64px' }}>
      <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--gold, #caa24a)' }}>Admin · internal</div>
          <h1 style={{ margin: '4px 0 2px', fontSize: 26 }}>Behavior</h1>
          <p style={{ margin: 0, color: 'var(--text-muted, #9a9aa2)', fontSize: 14 }}>
            Activation funnel over time — signup, onboarding, first value, paywall, subscribe — plus nudge/tour effectiveness.
          </p>
        </div>
        <button type="button" style={btn(funnelState.loading || funnelState.series.length === 0)} disabled={funnelState.loading || funnelState.series.length === 0} onClick={onExport}>
          Export CSV
        </button>
      </div>

      <FiltersPanel
        from={from}
        to={to}
        setFrom={setFromManual}
        setTo={setToManual}
        activePreset={activePreset}
        onPreset={onPreset}
        stages={stages}
        toggleStage={toggleStage}
        onSelectAll={() => setStages(FUNNEL_STAGES.map((s) => s.key))}
        onSelectNone={() => setStages([])}
        disabled={funnelState.loading}
      />

      {funnelState.error ? (
        <ErrorNote error={funnelState.error} />
      ) : (
        <FunnelChart series={funnelState.series} stages={orderedStages} />
      )}

      <SummaryTable stages={orderedStages} totals={funnelState.totals} />

      <SourceFunnelTable state={sourceState} />

      <EventsTable events={eventsState.events} loading={eventsState.loading} error={eventsState.error} total={eventsState.total} />
    </div>
  );
}

export default AdminBehavior;
